// src/screens/dashboard/StockPage.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Search, ListFilter } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ReactComponent as Devices } from "../../assets/images/Devices.svg";

import PageHeader from "../../components/layout/PageHeader";
import DataTable from "../../components/table/Datatable";
import PaginationFooter from "../../components/table/PaginationFooter";
import DateRangeButton from "../../components/inputs/DateRangeButton";
import SelectField from "../../components/inputs/SelectField";
import BatteryPill from "../../components/battery/battery";
import { ReactComponent as Tag } from "../../assets/images/Tag.svg";
import { ReactComponent as Right } from "../../assets/images/right.svg";

import useApiClient, { createHttpClient } from "../../api/client";

const PRODUCTS_ENDPOINT = "products";
const ACCESSORIES_ENDPOINT = "accessories";
const PRODUCT_ACCESSORIES_ENDPOINT = "product-accessories";
const PRODUCT_ACCESSORIES_ENDPOINT_FALLBACK = "product_accessories";
const CATEGORIES_ENDPOINT = "categories";

const DEFAULT_LINK_FILTER = { linked: true, unlinked: true };

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

function buildProductDisplayName(product) {
  const productName = String(product?.product_name || "").trim();
  const categoryName = String(product?.model?.category?.name || product?.category?.name || "").trim();
  const modelName = String(product?.model?.name || "").trim();
  if (productName) return productName;
  if (categoryName && modelName) return `${categoryName} ${modelName}`;
  return categoryName || modelName || "-";
}

function compareText(a, b) {
  const A = String(a ?? "").toLowerCase();
  const B = String(b ?? "").toLowerCase();
  if (A < B) return -1;
  if (A > B) return 1;
  return 0;
}

function formatWarrantyDisplay(info, expireDate) {
  const warranty = String(info || "").trim();
  if (warranty === "ประกันเหลือ" || warranty === "ประกันศูนย์") {
    if (!expireDate) return "ประกันศูนย์";
    const d = new Date(expireDate);
    if (Number.isNaN(d.getTime())) return "ประกันศูนย์";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `ประกันศูนย์ (${dd}/${mm}/${yyyy})`;
  }
  if (warranty === "ประกันหมด") return "ประกันหมด";
  if (warranty.includes("ประกันศูนย์")) return "ประกันศูนย์ 1 ปี";
  return "-";
}

function StatusPill({ value, linkStatus }) {
  const map = {
    ติดมัดจำ: "bg-[#8E16161A] text-[#8E1616]",
    ไม่พร้อมขาย: "bg-[#FF95021A] text-[#FF9502]",
    ร่าง: "bg-[#FBEF011A] text-[#B4A716]",
    พร้อมขาย: "bg-[#30CA171A] text-[#30CA17]",
    ขายแล้ว: "bg-[#FF16071A] text-[#FF1607]",
  };
  const tone = map[value] || "bg-slate-100 text-slate-700";

  const linkBorder =
    linkStatus === "linked"
      ? "border-l-2 border-[#30CA17]"
      : "border-l-2 border-[#CCCCCC]";

  return (
    <span className="inline-flex items-center gap-3">
      {linkStatus && <span className={classNames("h-10", linkBorder)} />}
      <span
        className={classNames(
          "inline-flex min-w-[92px] justify-center rounded-xl px-3 py-1 text-[12px] font-bold whitespace-nowrap",
          tone
        )}
      >
        {value || "-"}
      </span>
    </span>
  );
}

function AccessorySellModal({ open, onClose, value, onSubmit }) {
  const [saving, setSaving] = useState(false);
  const [openedAt, setOpenedAt] = useState("");
  const [form, setForm] = useState({
    customer_name: "",
    customer_phone: "",
    discount: "0",
    pay_cash: false,
    pay_transfer: false,
    pay_card: false,
    cash_amount: "0",
    transfer_amount: "0",
    card_amount: "0",
  });

  useEffect(() => {
    if (!open) return;
    setOpenedAt(new Date().toISOString());
    setForm({
      customer_name: String(value?.raw?.customer_name || ""),
      customer_phone: String(value?.raw?.customer_phone || ""),
      discount: String(value?.raw?.discount ?? "0"),
      pay_cash: false,
      pay_transfer: false,
      pay_card: false,
      cash_amount: "0",
      transfer_amount: "0",
      card_amount: "0",
    });
  }, [open, value]);

  if (!open || !value) return null;

  const price = Number(value?.price ?? value?.raw?.price ?? 0) || 0;
  const discount = Number(form.discount || 0) || 0;
  const netTotal = Math.max(0, price - discount);
  const totalPaid =
    (form.pay_cash ? Number(form.cash_amount || 0) || 0 : 0) +
    (form.pay_transfer ? Number(form.transfer_amount || 0) || 0 : 0) +
    (form.pay_card ? Number(form.card_amount || 0) || 0 : 0);
  const remaining = netTotal - totalPaid;

  return (
    <div className="fixed inset-0 z-[999]">
      <div className="absolute inset-0 bg-black/40" onClick={saving ? undefined : onClose} />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-[720px] rounded-2xl bg-white shadow-2xl flex flex-col max-h-[66.67vh] md:max-h-[90vh]">
          <div className="flex items-start justify-between p-6 shrink-0 sticky top-0 bg-white rounded-t-2xl z-10">
            <div className="text-xl font-extrabold truncate max-w-[520px]">
              {value?.item || value?.raw?.name || "-"}
            </div>
            <button
              type="button"
              onClick={saving ? undefined : onClose}
              className="h-10 rounded-xl border-2 border-slate-200 bg-white px-4 text-sm font-bold text-[#047DED] active:scale-[0.98] hover:bg-[#F5F5F5] hover:border-none"
            >
              ยกเลิก
            </button>
          </div>

          <div className="px-6 pb-6 space-y-6 overflow-y-auto">
            <div className="space-y-3">
              <div className="text-base font-extrabold text-slate-800">รายละเอียดสินค้า</div>
              <div className="flex items-center">
                <div className="inline-flex items-center gap-2 rounded-xl bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-800">
                  ราคา: {price.toLocaleString("th-TH")} บาท
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="text-base font-extrabold text-slate-800">ช่องทางการติดต่อ</div>
              <div className="space-y-2">
                <div className="text-sm font-bold text-slate-700">ชื่อลูกค้า:</div>
                <input
                  value={form.customer_name}
                  onChange={(e) => setForm((s) => ({ ...s, customer_name: e.target.value }))}
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-300"
                  placeholder="กรอกชื่อลูกค้า"
                />
              </div>
              <div className="space-y-2">
                <div className="text-sm font-bold text-slate-700">เบอร์โทรศัพท์ของผู้ซื้อ:</div>
                <input
                  value={form.customer_phone}
                  maxLength={10}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^0-9]/g, "");
                    setForm((s) => ({ ...s, customer_phone: v }));
                  }}
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-300"
                  placeholder="กรอกเบอร์โทรศัพท์"
                />
              </div>
              <div className="space-y-2">
                <div className="text-sm font-bold text-slate-700">ส่วนลดสินค้า (บาท):</div>
                <input
                  value={form.discount}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^0-9]/g, "");
                    setForm((s) => ({ ...s, discount: v }));
                  }}
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-300"
                  placeholder="0"
                />
              </div>
            </div>

            <div className="space-y-3">
              <div className="text-base font-extrabold text-slate-800">ช่องทางการชำระเงิน</div>
              <div className="space-y-4">
                {[
                  { key: "pay_cash", amtKey: "cash_amount", label: "เงินสด" },
                  { key: "pay_transfer", amtKey: "transfer_amount", label: "โอนเงิน" },
                  { key: "pay_card", amtKey: "card_amount", label: "บัตรเครดิต" },
                ].map(({ key, amtKey, label }) => (
                  <div key={key}>
                    <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer select-none">
                      <span
                        className={classNames(
                          "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
                          form[key] ? "border-[#047DED] bg-[#047DED]" : "border-slate-300 bg-white"
                        )}
                        onClick={() =>
                          setForm((s) => ({
                            ...s,
                            [key]: !s[key],
                            [amtKey]: !s[key] ? s[amtKey] : "0",
                          }))
                        }
                      >
                        {form[key] && (
                          <span className="h-2 w-2 rounded-full bg-white" />
                        )}
                      </span>
                      <span
                        onClick={() =>
                          setForm((s) => ({
                            ...s,
                            [key]: !s[key],
                            [amtKey]: !s[key] ? s[amtKey] : "0",
                          }))
                        }
                      >
                        {label}
                      </span>
                    </label>
                    <div className="mt-1 flex items-center rounded-xl border border-slate-200 bg-white px-4 py-3">
                      <span className="mr-1 text-sm text-slate-500">฿</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={form[amtKey]}
                        disabled={!form[key]}
                        onChange={(e) => {
                          const v = e.target.value.replace(/[^0-9]/g, "");
                          setForm((s) => ({ ...s, [amtKey]: v }));
                        }}
                        className="w-full bg-transparent text-sm outline-none disabled:text-slate-400"
                        placeholder="0"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 rounded-xl border border-slate-100 bg-slate-50 px-4 py-3 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">ยอดรวม</span>
                  <span className="font-bold text-slate-800">{netTotal.toLocaleString("th-TH")} บาท</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">จำนวนที่ชำระ</span>
                  <span className="font-bold text-slate-800">{totalPaid.toLocaleString("th-TH")} บาท</span>
                </div>
                <div className="h-px bg-slate-200" />
                <div className="flex justify-between">
                  <span className="font-bold text-slate-700">ยอดที่ต้องขาย</span>
                  <span
                    className={classNames(
                      "font-extrabold",
                      remaining > 0 ? "text-red-500" : remaining < 0 ? "text-orange-500" : "text-green-600"
                    )}
                  >
                    {remaining.toLocaleString("th-TH")} บาท
                  </span>
                </div>
              </div>
            </div>

            <div className="pt-2 flex items-center justify-end gap-4">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="h-12 rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold hover:bg-slate-50 disabled:opacity-60"
              >
                ยกเลิก
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={async () => {
                  try {
                    setSaving(true);
                    const selectedMethods = [];
                    if (form.pay_cash) selectedMethods.push({ key: "cash", label: "เงินสด", amount: Number(form.cash_amount || 0) || 0 });
                    if (form.pay_transfer) selectedMethods.push({ key: "transfer", label: "โอนเงิน", amount: Number(form.transfer_amount || 0) || 0 });
                    if (form.pay_card) selectedMethods.push({ key: "credit_card", label: "บัตรเครดิต", amount: Number(form.card_amount || 0) || 0 });

                    if (!selectedMethods.length) {
                      alert("กรุณาเลือกช่องทางการชำระอย่างน้อย 1 ช่องทาง");
                      return;
                    }

                    const methodsText = selectedMethods
                      .map((m) => `${m.label} ${Number(m.amount || 0).toLocaleString("th-TH")} บาท`)
                      .join(", ");

                    const paymentMethodForApi =
                      selectedMethods.length === 1 ? selectedMethods[0].key : undefined;

                    await onSubmit?.({
                      product_accessory_id: String(value?.id || value?.raw?.id || ""),
                      qty: 1,
                      payment_method: paymentMethodForApi,
                      price_cash: Number(form.cash_amount || 0) || 0,
                      price_transfer: Number(form.transfer_amount || 0) || 0,
                      price_card: Number(form.card_amount || 0) || 0,
                      customer_name: String(form.customer_name || "").trim(),
                      customer_phone: String(form.customer_phone || "").trim(),
                      date: openedAt || new Date().toISOString(),
                      trade_discount: Number(form.discount || 0) || 0,
                      deposit: 0,
                      note:
                        selectedMethods.length > 1
                          ? `ชำระหลายช่องทาง: ${methodsText}`
                          : methodsText,
                    });
                  } catch (err) {
                    console.error(err);
                    alert("บันทึกการขายไม่สำเร็จ");
                  } finally {
                    setSaving(false);
                  }
                }}
                className={classNames(
                  "h-12 rounded-xl px-4 text-sm font-bold text-white shadow-md active:scale-[0.99]",
                  saving ? "bg-slate-400" : "bg-[#047DED] hover:opacity-95"
                )}
              >
                {saving ? "กำลังบันทึก..." : "บันทึกข้อมูลการขาย"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SellButton({ disabled, onClick }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={classNames(
        "h-10 min-w-[72px] rounded-xl px-8 text-sm font-bold",
        disabled
          ? "bg-[#A9A9A9] text-white cursor-not-allowed"
          : "bg-[#047DED] text-white shadow-md shadow-[#2F6FED55] hover:opacity-95 active:scale-[0.98]"
      )}
    >
      ขาย
    </button>
  );
}

function ManageButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="h-10 rounded-xl border-2 border-slate-200 bg-white px-4 text-sm font-bold text-[#047DED] active:scale-[0.98] hover:bg-[#F5F5F5] hover:border-none"
    >
      จัดการ
    </button>
  );
}

// ---------- Modal (เพิ่ม/แก้ไข อุปกรณ์เสริม) ----------
function AccessoryUpsertModal({
  open,
  onClose,
  value, // edit: มี id, create: null
  categoryOptions = [],
  onSubmit,
}) {
  const isEdit = !!value?.id;
  const http = React.useMemo(() => createHttpClient(), []);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [brandsOptions, setBrandsOptions] = useState([]);
  const [brandsLoading, setBrandsLoading] = useState(false);

  useEffect(() => {
    if (!open) return;

    if (!value) {
      setForm({
        name: "",
        accessory_category: "",
        brands: "",
        price: "",
        qty: 0,
        remark: "",
      });
      setErrors({});
      return;
    }

    setForm({
      name: value.item || "",
      accessory_category: String(value.categoryId || ""),
      brands: String(value.brandId || ""),
      price: value.price ?? "",
      qty: value.qty ?? 0,
      remark: String(value.remark ?? value?.raw?.remark ?? ""),
    });
    setErrors({});
  }, [open, value]);

  useEffect(() => {
    if (!open) return;
    if (!form) return;
    if (form.accessory_category) return;
    if (categoryOptions.length === 0) return;

    setForm((s) => ({ ...s, accessory_category: String(categoryOptions[0].value), brands: "" }));
  }, [open, form, categoryOptions]);

  useEffect(() => {
    if (!open || !form) return;
    const categoryId = String(form.accessory_category || "").trim();
    if (!categoryId) {
      setBrandsOptions([]);
      if (form.brands) setForm((s) => ({ ...s, brands: "" }));
      return;
    }

    let ignore = false;
    (async () => {
      try {
        setBrandsLoading(true);
        const res = await http.get("/brands", {
          params: {
            filters: { accessory: { id: { $eq: categoryId } } },
            sort: ["name:asc"],
            pagination: { pageSize: 200 },
          },
        });
        if (ignore) return;
        const list = Array.isArray(res?.data?.data) ? res.data.data : [];
        const opts = list.map((it) => ({
          value: String(it?.id ?? ""),
          label: String(it?.name || "-"),
        })).filter((it) => it.value);
        setBrandsOptions(opts);
        setForm((prev) => {
          if (!prev) return prev;
          const currentBrands = String(prev.brands || "");
          const exists = opts.some((op) => op.value === currentBrands);
          if (exists) return prev;
          return { ...prev, brands: opts[0]?.value || "" };
        });
      } catch (err) {
        if (ignore) return;
        console.error(err);
        setBrandsOptions([]);
        setForm((prev) => (prev ? { ...prev, brands: "" } : prev));
      } finally {
        if (!ignore) setBrandsLoading(false);
      }
    })();

    return () => {
      ignore = true;
    };
  }, [open, form?.accessory_category]);

  if (!open || !form) return null;

  const qtyNum = Number(form.qty) || 0;

  const validateForm = () => {
    const nextErrors = {};
    if (!String(form.name || "").trim()) nextErrors.name = "required";
    if (!String(form.accessory_category || "").trim()) nextErrors.accessory_category = "required";
    if (!String(form.brands || "").trim()) nextErrors.brands = "required";
    if (String(form.price || "").trim() === "") nextErrors.price = "required";
    if (String(form.qty || "").trim() === "") nextErrors.qty = "required";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  return (
    <div className="fixed inset-0 z-[999]">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={saving ? undefined : onClose}
      />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-[620px] rounded-2xl bg-white shadow-2xl">
          <div className="flex items-start justify-between p-6">
            <div className="text-xl font-extrabold">
              {isEdit ? "แก้ไขอุปกรณ์เสริม" : "เพิ่มอุปกรณ์เสริม"}
            </div>

            <button
              type="button"
              onClick={saving ? undefined : onClose}
              className="rounded-xl p-2 hover:bg-slate-100 disabled:opacity-60"
              aria-label="close"
              disabled={saving}
            >
              ✕
            </button>
          </div>

          <div className="px-6 pb-6 space-y-5">
            <div className="space-y-2">
              <div className="text-sm font-bold text-slate-700">ชื่อสินค้าอุปกรณ์เสริม</div>
              <input
                value={form.name}
                onChange={(e) => {
                  const next = e.target.value;
                  setForm((s) => ({ ...s, name: next }));
                  setErrors((prev) => ({ ...prev, name: undefined }));
                }}
                className={classNames(
                  "w-full rounded-xl border px-4 py-3 text-sm outline-none focus:border-blue-300",
                  errors.name ? "border-red-500" : "border-slate-200"
                )}
                placeholder="เช่น Airpod / หัวชาร์จ 20W / เคสใส"
              />
              {errors.name && <div className="text-xs text-red-600">required</div>}
            </div>

            <div className="space-y-2">
              <div className="text-sm font-bold text-slate-700">ประเภท</div>
              <div className="relative">
                <select
                  value={form.accessory_category}
                  onChange={(e) =>
                    {
                      setForm((s) => ({ ...s, accessory_category: e.target.value, brands: "" }));
                      setErrors((prev) => ({ ...prev, accessory_category: undefined, brands: undefined }));
                    }
                  }
                  className={classNames(
                    "w-full appearance-none rounded-xl border bg-white px-4 py-3 text-sm outline-none focus:border-blue-300",
                    errors.accessory_category ? "border-red-500" : "border-slate-200"
                  )}
                >
                  {categoryOptions.length === 0 ? (
                    <option value="">กำลังโหลดประเภท...</option>
                  ) : (
                    categoryOptions.map((op) => (
                      <option key={op.value} value={String(op.value)}>
                        {op.label}
                      </option>
                    ))
                  )}
                </select>
                <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#1D161680]">
                  ▾
                </div>
              </div>
              {errors.accessory_category && <div className="text-xs text-red-600">required</div>}
            </div>

            <div className="space-y-2">
              <div className="text-sm font-bold text-slate-700">แบรนด์</div>
              <div className="relative">
                <select
                  value={form.brands}
                  onChange={(e) => {
                    setForm((s) => ({ ...s, brands: e.target.value }));
                    setErrors((prev) => ({ ...prev, brands: undefined }));
                  }}
                  className={classNames(
                    "w-full appearance-none rounded-xl border bg-white px-4 py-3 text-sm outline-none focus:border-blue-300",
                    errors.brands ? "border-red-500" : "border-slate-200"
                  )}
                >
                  {!form.accessory_category ? (
                    <option value="">กรุณาเลือกประเภทก่อน</option>
                  ) : brandsLoading ? (
                    <option value="">กำลังโหลดแบรนด์...</option>
                  ) : brandsOptions.length === 0 ? (
                    <option value="">ไม่พบแบรนด์</option>
                  ) : (
                    brandsOptions.map((op) => (
                      <option key={op.value} value={String(op.value)}>
                        {op.label}
                      </option>
                    ))
                  )}
                </select>
                <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#1D161680]">
                  ▾
                </div>
              </div>
              {errors.brands && <div className="text-xs text-red-600">required</div>}
            </div>

            <div className="space-y-2">
              <div className="text-sm font-bold text-slate-700">ราคาต่อหน่วย</div>
              <input
                inputMode="numeric"
                value={form.price}
                onChange={(e) => {
                  const next = String(e.target.value || "").replace(/[^0-9]/g, "");
                  setForm((s) => ({ ...s, price: next }));
                  setErrors((prev) => ({ ...prev, price: undefined }));
                }}
                className={classNames(
                  "w-full rounded-xl border px-4 py-3 text-sm outline-none focus:border-blue-300",
                  errors.price ? "border-red-500" : "border-slate-200"
                )}
                placeholder="฿0"
              />
              {errors.price && <div className="text-xs text-red-600">required</div>}
            </div>

            <div className="space-y-2">
              <div className="text-sm font-bold text-slate-700">
                จำนวน {isEdit ? `(ปัจจุบัน ${value?.qty ?? 0})` : ""}
              </div>
              <input
                type="text"
                inputMode="numeric"
                value={form.qty}
                onChange={(e) => {
                  const next = String(e.target.value || "").replace(/[^0-9]/g, "");
                  setForm((s) => ({ ...s, qty: next }));
                  setErrors((prev) => ({ ...prev, qty: undefined }));
                }}
                className={classNames(
                  "w-full rounded-xl border px-4 py-3 text-sm outline-none focus:border-blue-300",
                  errors.qty ? "border-red-500" : "border-slate-200"
                )}
                placeholder="0"
              />
              {errors.qty && <div className="text-xs text-red-600">required</div>}
              {/* <div className="text-xs text-[#1D161680]">
                สถานะในตารางจะแสดงเป็น{" "}
                <span className="font-bold">{qtyNum <= 0 ? "ขายแล้ว" : "พร้อมขาย"}</span>
              </div> */}
            </div>

            <div className="space-y-2">
              <div className="text-sm font-bold text-slate-700">หมายเหตุ</div>
              <textarea
                value={form.remark ?? ""}
                onChange={(e) => {
                  const next = e.target.value;
                  setForm((s) => ({ ...s, remark: next }));
                }}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-blue-300"
                placeholder="ระบุหมายเหตุเพิ่มเติม"
                rows={3}
              />
            </div>

            <div className="pt-4 flex items-center justify-between gap-4">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="h-12 flex-1 rounded-xl border border-slate-200 bg-white text-sm font-bold hover:bg-slate-50 disabled:opacity-60"
              >
                ยกเลิก
              </button>

              <button
                type="button"
                disabled={saving}
                onClick={async () => {
                  try {
                    setSaving(true);
                    if (!validateForm()) {
                      return;
                    }
                    const priceNum = Number(String(form.price ?? "").replace(/,/g, "")) || 0;

                    await onSubmit?.({
                      id: value?.id,
                      data: {
                        name: String(form.name || "").trim(),
                        accessory_category: Number(form.accessory_category),
                        brand: Number(form.brands),
                        price: priceNum,
                        qty: Number(form.qty) || 0,
                        stock_status: "available",
                        remark: String(form.remark || "").trim(),
                      },
                    });

                    onClose();
                  } catch (err) {
                    console.error(err);
                    alert("บันทึกไม่สำเร็จ");
                  } finally {
                    setSaving(false);
                  }
                }}
                className={classNames(
                  "h-12 flex-1 rounded-xl text-sm font-bold text-white shadow-md active:scale-[0.99]",
                  saving
                    ? "bg-slate-400"
                    : "bg-gradient-to-r from-[#111] to-[#3a2f2f] hover:opacity-95"
                )}
              >
                {saving ? "กำลังบันทึก..." : "บันทึก"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- mapper: product_status -> ไทย ----------
function mapProductStatusThai(product_status) {
  const s = String(product_status || "").toLowerCase();
  if (["available", "ready", "in_stock"].includes(s)) return "พร้อมขาย";
  if (["draft"].includes(s)) return "ร่าง";
  if (["deposit", "reserved", "hold"].includes(s)) return "ติดมัดจำ";
  if (["unavailable", "not_available", "disabled"].includes(s)) return "ไม่พร้อมขาย";
  if (["sold", "sold_out", "out_of_stock", "out"].includes(s)) return "ขายแล้ว";
  return "ไม่พร้อมขาย";
}

function uniqOptions(list, getKey, getLabel) {
  const m = new Map();
  (list || []).forEach((it) => {
    const k = getKey(it);
    if (!k) return;
    if (!m.has(String(k))) {
      m.set(String(k), { value: String(k), label: getLabel(it) });
    }
  });
  return Array.from(m.values()).sort((a, b) => compareText(a.label, b.label));
}

export default function StockPage() {
  const navigate = useNavigate();
  const api = useApiClient();

  // ✅ main products จาก /products
  const [productRows, setProductRows] = useState([]);
  const [prodLoading, setProdLoading] = useState(false);

  // ✅ accessory list จาก /product-accessories
  const [accessoryRows, setAccessoryRows] = useState([]);
  const [accLoading, setAccLoading] = useState(false);

  // ✅ dropdown จาก /accessories
  const [accCategories, setAccCategories] = useState([]);
  const [accCategoriesLoading, setAccCategoriesLoading] = useState(false);

  // filters
  const [query, setQuery] = useState("");
  const [range, setRange] = useState([null, null]);

  const buildDateParams = useCallback((fieldName, start, end) => {
    if (!start || !end) return {};

    const s = new Date(start);
    const e = new Date(end);
    s.setHours(0, 0, 0, 0);
    e.setHours(23, 59, 59, 999);

    return {
      [`filters[${fieldName}][$gte]`]: s.toISOString(),
      [`filters[${fieldName}][$lte]`]: e.toISOString(),
    };
  }, []);

  // tab
  const [tab, setTab] = useState("main");

  // main filters
  const [type, setType] = useState(""); // จะเก็บ categoryId (string)
  const [model, setModel] = useState(""); // จะเก็บ modelId (string)
  const [status, setStatus] = useState("");
  const [linkFilter, setLinkFilter] = useState(DEFAULT_LINK_FILTER);

  // accessory filters
  const [accCategoryFilter, setAccCategoryFilter] = useState("");

  // modal state
  const [editOpen, setEditOpen] = useState(false);
  const [selectedAccessory, setSelectedAccessory] = useState(null);
  const [sellOpen, setSellOpen] = useState(false);
  const [selectedSell, setSelectedSell] = useState(null);

  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  // sort (เฉพาะ tab main)
  const [sortKey, setSortKey] = useState("");
  const [sortDir, setSortDir] = useState("asc");
  const onSort = useCallback((key) => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        return prev;
      }
      setSortDir("asc");
      return key;
    });
  }, []);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const fetchCategories = useCallback(async () => {
    try {
      setCategoriesLoading(true);
      const res = await api.get(CATEGORIES_ENDPOINT);
      const list = res?.data?.data || [];

      setCategoryOptions(
        list.map((it) => ({
          value: String(it.id),
          label: it.name || "-",
        }))
      );
    } catch (e) {
      console.error(e);
      setCategoryOptions([]);
    } finally {
      setCategoriesLoading(false);
    }
  }, [api]);

  // ---------- map rows ----------
  const mapProductRow = useCallback((x) => {
    const modelObj = x?.model || {};
    const categoryObj = modelObj?.category || {};
    const colorObj = x?.color || {};
    const capObj = x?.capacity || {};
    const equipmentNames = Array.isArray(x?.equipments)
      ? x.equipments
          .map((it) => String(it?.name || "").trim())
          .filter(Boolean)
          .join(", ")
      : "";
    const displayName = buildProductDisplayName(x);

    const statusThai = mapProductStatusThai(x?.product_status);

    // link_status (เดิมมี legend) -> เดา: ถ้ามี facebook_ref ถือว่า "linked"
    const link_status = x?.facebook_ref ? "linked" : "unlinked";

    // ราคาในตาราง: แสดงราคาขายเท่านั้น
    const price = Number(x?.sell_price ?? 0) || 0;

    return {
      id: x?.id,
      link_status,
      status: statusThai,
      item: displayName,
      name: displayName,
      cap: capObj?.size || "-", // เช่น 256GB
      color: colorObj?.name || "-", // เช่น ดำ
      battery: x?.battery_health ?? null,
      gear: equipmentNames || x?.equipment || "-",
      warranty: formatWarrantyDisplay(x?.warranty_info, x?.warranty_date),
      imei: x?.imei || "-",
      price,

      // สำหรับ filter
      type: String(categoryObj?.id || ""), // categoryId
      typeLabel: categoryObj?.name || "-",
      modelKey: String(modelObj?.id || ""), // modelId
      modelLabel: displayName,

      // สำหรับ date filter
      created_at: x?.received_at || x?.createdAt || x?.publishedAt || null,

      // canSell (เปิดขายเฉพาะพร้อมขาย)
      canSell: statusThai === "พร้อมขาย",

      raw: x,
    };
  }, []);

  const mapAccessoryRow = useCallback((x) => {
    const qty = Number(x?.qty ?? 0) || 0;
    const uiStatus = qty <= 0 ? "หมด" : "พร้อมขาย";

    const cat = x?.accessory_category;
    const catId =
      typeof cat === "object" && cat ? cat.id : cat != null ? cat : "";
    const catName = typeof cat === "object" && cat ? cat.name : "-";
    const brands = x?.brand;
    const brandsId =
      typeof brands === "object" && brands ? brands.id : brands != null ? brands : "";
    const brandsName = typeof brands === "object" && brands ? brands.name : "-";

    return {
      id: x?.id,
      status: uiStatus,
      item: x?.name || "-",
      qty,
      category: catName || "-",
      categoryId: catId ? String(catId) : "",
      brands: brandsName || "-",
      brand: brandsName || "-",
      brandsId: brandsId ? String(brandsId) : "",
      brandId: brandsId ? String(brandsId) : "",
      price: Number(x?.price ?? 0) || 0,
      remark: String(x?.remark || ""),
      canSell: qty > 0,
      type: "accessory",
      raw: x,
    };
  }, []);

  // ---------- fetch ----------
  const fetchProducts = useCallback(async () => {
    try {
      setProdLoading(true);
      const [start, end] = Array.isArray(range) ? range : [null, null];
      const params = {
        ...buildDateParams("createdAt", start, end),
      };
      const res = await api.get(PRODUCTS_ENDPOINT, params);
      const list = res?.data?.data || [];
      const visibleMainProducts = (Array.isArray(list) ? list : []).filter((item) => {
        const normalized = String(item?.product_status || "").trim().toLowerCase();
        return !["sold", "sold_out", "out_of_stock", "out"].includes(normalized);
      });
      setProductRows(visibleMainProducts.map(mapProductRow));
    } catch (e) {
      console.error(e);
      setProductRows([]);
    } finally {
      setProdLoading(false);
    }
  }, [api, buildDateParams, mapProductRow, range]);

  const fetchAccessoryCategories = useCallback(async () => {
    try {
      setAccCategoriesLoading(true);
      const res = await api.get(ACCESSORIES_ENDPOINT);
      const list = res?.data?.data || [];

      setAccCategories(
        list.map((it) => ({
          value: String(it.id),
          label: it.name,
        }))
      );
    } catch (e) {
      console.error(e);
      setAccCategories([]);
    } finally {
      setAccCategoriesLoading(false);
    }
  }, [api]);

  const fetchProductAccessories = useCallback(async () => {
    const requestAccessoryList = async (params) => {
      try {
        return await api.get(PRODUCT_ACCESSORIES_ENDPOINT, params);
      } catch (firstErr) {
        return await api.get(PRODUCT_ACCESSORIES_ENDPOINT_FALLBACK, params);
      }
    };

    try {
      setAccLoading(true);
      const [start, end] = Array.isArray(range) ? range : [null, null];
      const params = {
        ...buildDateParams("createdAt", start, end),
      };
      const res = await requestAccessoryList(params);
      const list = res?.data?.data || [];
      setAccessoryRows(list.map(mapAccessoryRow));
    } catch (e) {
      console.error(e);
      setAccessoryRows([]);
    } finally {
      setAccLoading(false);
    }
  }, [api, buildDateParams, mapAccessoryRow, range]);

  // ✅ โหลดครั้งแรก
  useEffect(() => {
    fetchCategories();
    fetchAccessoryCategories();
  }, []);

  useEffect(() => {
    const [start, end] = Array.isArray(range) ? range : [null, null];
    const shouldRefetch = (!start && !end) || (start && end);
    if (!shouldRefetch) return;

    fetchProducts();
    fetchProductAccessories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range?.[0], range?.[1]]);

  // ✅ สลับแท็บ: ล้าง filter ที่ไม่เกี่ยว + ล้าง sort ตอนออกจาก main
  useEffect(() => {
    setPage(1);

    if (tab === "main") {
      setAccCategoryFilter("");
    } else {
      setType("");
      setModel("");
      setLinkFilter(DEFAULT_LINK_FILTER);
      setSortKey("");
      setSortDir("asc");
    }
  }, [tab]);

  const openCreateAccessory = useCallback(() => {
    setSelectedAccessory(null);
    setEditOpen(true);

    if (accCategories.length === 0 && !accCategoriesLoading) {
      fetchAccessoryCategories();
    }
  }, [accCategories.length, accCategoriesLoading]);

  const openEditAccessory = useCallback(
    (row) => {
      setSelectedAccessory(row);
      setEditOpen(true);

      if (accCategories.length === 0 && !accCategoriesLoading) {
        fetchAccessoryCategories();
      }
    },
    [accCategories.length, accCategoriesLoading]
  );

  const openSellAccessory = useCallback((row) => {
    setSelectedSell(row);
    setSellOpen(true);
  }, []);

  const allRows = useMemo(() => {
    return tab === "main" ? productRows : accessoryRows;
  }, [tab, productRows, accessoryRows]);

  const modelOptions = useMemo(() => {
    const rows = type ? productRows.filter((r) => String(r.type) === String(type)) : productRows;
    return uniqOptions(rows, (r) => r.modelKey, (r) => r.modelLabel || "-");
  }, [productRows, type]);

  const statusOptionsMain = useMemo(
    () => [
      { value: "พร้อมขาย", label: "พร้อมขาย" },
      { value: "ไม่พร้อมขาย", label: "ไม่พร้อมขาย" },
      { value: "ติดมัดจำ", label: "ติดมัดจำ" },
      { value: "ร่าง", label: "ร่าง" },
      { value: "หมด", label: "หมด" },
    ],
    []
  );

  const statusOptionsAccessory = useMemo(
    () => [
      { value: "พร้อมขาย", label: "พร้อมขาย" },
      { value: "หมด", label: "หมด" },
    ],
    []
  );

  const inRange = useCallback(
    (dateLike) => {
      const [start, end] = range || [];
      if (!start || !end) return true;

      const d = dateLike ? new Date(dateLike) : null;
      if (!d || Number.isNaN(d.getTime())) return true;

      const s = new Date(start);
      const e = new Date(end);
      s.setHours(0, 0, 0, 0);
      e.setHours(23, 59, 59, 999);

      return d >= s && d <= e;
    },
    [range]
  );

  // filter
  const baseFiltered = useMemo(() => {
    let rows = [...allRows];

    // ✅ date filter
    if (range?.[0] && range?.[1]) {
      if (tab === "main") {
        rows = rows.filter((r) => inRange(r.created_at));
      } else {
        rows = rows.filter((r) => inRange(r.raw?.createdAt || r.raw?.created_at));
      }
    }

    if (tab === "main") {
      if (type) rows = rows.filter((r) => String(r.type) === String(type));
      if (model) rows = rows.filter((r) => String(r.modelKey) === String(model));
      if (status) rows = rows.filter((r) => r.status === status);

      rows = rows.filter((r) => {
        const key = (r.link_status || "unlinked") === "linked" ? "linked" : "unlinked";
        return Boolean(linkFilter?.[key]);
      });
    } else {
      if (accCategoryFilter) {
        rows = rows.filter((r) => String(r.categoryId || "") === String(accCategoryFilter));
      }

      if (status) rows = rows.filter((r) => r.status === status);
    }

    if (query.trim()) {
      const q = query.trim().toLowerCase();
      rows = rows.filter((r) =>
        [r.item, r.cap, r.color, r.imei, r.typeLabel, r.modelLabel, r.category, r.qty, r.price]
          .map((x) => String(x ?? "").toLowerCase())
          .some((x) => x.includes(q))
      );
    }

    return rows;
  }, [
    allRows,
    tab,
    type,
    model,
    status,
    linkFilter,
    query,
    accCategoryFilter,
    range,
    inRange,
  ]);

  // sort หลัง filter (เฉพาะ tab main)
  const sortedFiltered = useMemo(() => {
    if (tab !== "main") return baseFiltered;
    if (!sortKey) return baseFiltered;

    const next = [...baseFiltered].sort((a, b) => {
      const c = compareText(a?.[sortKey], b?.[sortKey]);
      return sortDir === "asc" ? c : -c;
    });

    return next;
  }, [baseFiltered, tab, sortKey, sortDir]);

  // counts
  const tabCounts = useMemo(() => {
    return {
      main: productRows.length,
      accessory: accessoryRows.length,
    };
  }, [productRows, accessoryRows]);

  // pagination
  const totalItems = sortedFiltered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.max(1, Math.min(page, totalPages));

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safePage]);

  const paged = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return sortedFiltered.slice(start, start + pageSize);
  }, [sortedFiltered, safePage, pageSize]);

  // columns
  const columnsMain = useMemo(
    () => [
      {
        key: "status",
        title: "สถานะ",
        headerClassName: "w-[160px]",
        pl: "pl-10",
        render: (r) => <StatusPill value={r.status} linkStatus={r.link_status} />,
      },
      {
        key: "name",
        title: "รายการสินค้า",
        headerClassName: "w-[220px]",
        className: "font-semibold",
        sortable: true,
        sortKey: "name",
      },
      { key: "cap", title: "ความจุ", headerClassName: "w-[110px]" },
      { key: "color", title: "สี", headerClassName: "w-[80px]" },
      {
        key: "battery",
        title: "สุขภาพแบต",
        headerClassName: "w-[140px]",
        render: (r) => (r.battery != null ? <BatteryPill value={r.battery} /> : "-"),
      },
      { key: "gear", title: "อุปกรณ์", headerClassName: "w-[140px]" },
      {
        key: "warranty",
        title: "ประกัน",
        headerClassName: "w-[160px]",
        sortable: true,
        sortKey: "warranty",
      },
      { key: "imei", title: "IMEI", headerClassName: "w-[180px]" },
      {
        key: "price",
        title: "ราคา",
        headerClassName: "w-[140px]",
        className: "font-semibold",
        render: (r) => (r.price ? `฿${Number(r.price).toLocaleString("th-TH")}` : "-"),
      },
      {
        key: "sell",
        title: "ขาย",
        headerClassName: "w-[140px]",
        render: (r) => (
          <SellButton
            disabled={!r.canSell}
            onClick={() => navigate("/app/products/stock-sell-out", { state: { product: r.raw } })}
          />
        ),
      },
      {
        key: "manage",
        title: "จัดการ",
        headerClassName: "w-[160px]",
        render: (r) => (
          <ManageButton
            onClick={() =>
              navigate(`/app/products/stock-buy-in/${r.raw.id}`, {
                state: { product: r.raw },
              })
            }
          />
        ),
      },
    ],
    [navigate]
  );

  const columnsAccessory = useMemo(
    () => [
      {
        key: "status",
        title: "สถานะ",
        pl: "pl-10",
        headerClassName: "w-[200px]",
        render: (r) => <StatusPill value={r.status} />,
      },
      {
        key: "item",
        title: "รายการสินค้า",
        className: "font-semibold",
        headerClassName: "w-[450px]",
      },
      { key: "qty", title: "คงเหลือ", render: (r) => r.qty ?? 0, headerClassName: "w-[200px]" },
      {
        key: "category",
        title: "ประเภท",
        render: (r) => r.category || "-",
        headerClassName: "w-[220px]",
      },
      {
        key: "price",
        title: "ราคา",
        headerClassName: "w-[200px]",
        className: "font-semibold",
        render: (r) => `฿${Number(r.price || 0).toLocaleString("th-TH")}`,
      },
      {
        key: "sell",
        title: "ขาย",
        headerClassName: "w-[160px]",
        render: (r) => (
          <SellButton disabled={!r.canSell} onClick={() => openSellAccessory(r)} />
        ),
      },
      {
        key: "manage",
        title: "จัดการ",
        headerClassName: "w-[150px]",
        pl: "pl-6",
        render: (r) => (
          <div className="flex items-center gap-2 text-center justify-start">
            <ManageButton onClick={() => openEditAccessory(r)} />
          </div>
        ),
      },
    ],
    [openEditAccessory, openSellAccessory]
  );

  const columns = tab === "main" ? columnsMain : columnsAccessory;

  return (
    <div className="space-y-4 p-4 bg-white min-h-full">
      <PageHeader
        icon={Devices}
        breadcrumb="รายการสินค้า"
        title="สต๊อกสินค้า"
        showExport={false}
        exportPath="/export/stock"
        extraActions={
          <button
            type="button"
            onClick={() => {
              if (tab === "main") {
                navigate("/app/products/stock-buy-in");
              } else {
                openCreateAccessory();
              }
            }}
            className="inline-flex items-center gap-2 rounded-xl bg-[#111111] px-4 py-[10px] text-sm font-bold text-white shadow-md hover:opacity-95 active:scale-[0.99]"
          >
            <Right className="h-5 w-5 text-white" />
            {tab === "main" ? "ซื้อสินค้าหลักเข้า" : "ซื้ออุปกรณ์เสริมเข้า"}
          </button>
        }
      />

      {/* search + time */}
      <div className="rounded-2xl bg-[#F5F5F5] p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="w-full md:max-w-[520px]">
            <div className="flex items-center gap-3 rounded-xl border hover:border-2 border-[#1D161633] bg-white px-4 py-3">
              <Search size={18} className="text-[#1D1616B2]" />
              <input
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setPage(1);
                }}
                className="w-full bg-transparent text-sm outline-none placeholder:text-[#1D161680]"
                placeholder="ค้นหารายการ..."
              />
            </div>
          </div>

          <div className="flex w-full justify-start md:w-auto md:justify-end">
            <DateRangeButton mode="single" range={range} setRange={setRange} />
          </div>
        </div>
      </div>

      {/* selects */}
      {tab === "main" ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <SelectField
            icon={ListFilter}
            label="ชื่อสินค้า"
            placeholder={categoriesLoading ? "กำลังโหลด..." : "ชื่อสินค้า"}
            options={categoryOptions}
            value={type}
            onChange={(v) => {
              setType(v);
              setModel("");
              setPage(1);
            }}
          />
          <SelectField
            icon={ListFilter}
            label="รุ่นสินค้า"
            placeholder={prodLoading ? "กำลังโหลด..." : "รุ่นสินค้า"}
            options={modelOptions}
            value={model}
            onChange={(v) => {
              setModel(v);
              setPage(1);
            }}
          />
          <SelectField
            icon={Tag}
            label="สถานะ"
            placeholder="สถานะ"
            options={statusOptionsMain}
            value={status}
            onChange={(v) => {
              setStatus(v);
              setPage(1);
            }}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <SelectField
            icon={ListFilter}
            label="ประเภท"
            placeholder={accCategoriesLoading ? "กำลังโหลด..." : "ประเภท"}
            options={accCategories}
            value={accCategoryFilter}
            onChange={(v) => {
              setAccCategoryFilter(v);
              setPage(1);
            }}
          />
          <SelectField
            icon={Tag}
            label="สถานะ"
            placeholder="สถานะ"
            options={statusOptionsAccessory}
            value={status}
            onChange={(v) => {
              setStatus(v);
              setPage(1);
            }}
          />
        </div>
      )}

      {/* tabs + legend row */}
      <div className="flex items-center justify-between gap-3">
        <div className="border-b border-slate-200 flex-1">
          <div className="flex items-center gap-6">
            <button
              type="button"
              onClick={() => setTab("main")}
              className={classNames(
                "relative py-3 text-sm font-bold",
                tab === "main" ? "text-blue-600" : "text-[#1D161680]"
              )}
            >
              สินค้าหลัก
              <span
                className={classNames(
                  "ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-[12px] font-extrabold",
                  tab === "main" ? "bg-[#047DED] text-white" : "bg-[#8E8B8B] text-white"
                )}
              >
                {tabCounts.main}
              </span>
              {tab === "main" && (
                <span className="absolute left-0 right-0 -bottom-[1px] h-[2px] bg-blue-600" />
              )}
            </button>

            <button
              type="button"
              onClick={() => setTab("accessory")}
              className={classNames(
                "relative py-3 text-sm font-bold",
                tab === "accessory" ? "text-blue-600" : "text-[#1D161680]"
              )}
            >
              อุปกรณ์เสริม
              <span
                className={classNames(
                  "ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-[12px] font-extrabold",
                  tab === "accessory" ? "bg-[#047DED] text-white" : "bg-[#8E8B8B] text-white"
                )}
              >
                {tabCounts.accessory}
              </span>
              {tab === "accessory" && (
                <span className="absolute left-0 right-0 -bottom-[1px] h-[2px] bg-blue-600" />
              )}
            </button>
          </div>
        </div>

        {/* legend right (เฉพาะ main) */}
        {tab === "main" && (
          <div className="hidden md:flex items-center gap-4 text-sm">
            <button
              type="button"
              onClick={() => {
                setLinkFilter((prev) => {
                  const isOnlyLinked = Boolean(prev?.linked) && !Boolean(prev?.unlinked);
                  return isOnlyLinked
                    ? DEFAULT_LINK_FILTER
                    : { linked: true, unlinked: false };
                });
                setPage(1);
              }}
              className="flex items-center gap-2 rounded-xl px-3 py-2 transition"
            >
              <span className="h-4 w-1 rounded-full bg-[#047DED]" />
              <span className="font-bold text-[#047DED]">
                แนบลิงค์แล้ว
              </span>
            </button>

            <button
              type="button"
              onClick={() => {
                setLinkFilter((prev) => {
                  const isOnlyUnlinked = Boolean(prev?.unlinked) && !Boolean(prev?.linked);
                  return isOnlyUnlinked
                    ? DEFAULT_LINK_FILTER
                    : { linked: false, unlinked: true };
                });
                setPage(1);
              }}
              className="flex items-center gap-2 rounded-xl px-3 py-2 transition"
            >
              <span className="h-4 w-1 rounded-full bg-[#8E8B8B]" />
              <span className="font-bold text-[#8E8B8B]">
                ยังไม่ได้แนบลิงค์
              </span>
            </button>
          </div>
        )}
      </div>
      {/* table */}
      {tab === "main" ? (
        <div className="space-y-2">
          {prodLoading && (
            <div className="text-sm text-[#1D161680] px-2">กำลังโหลดสินค้าหลัก...</div>
          )}
          <div className="relative">
            <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-[3px] rounded-full" />
            <DataTable
              columns={columns}
              data={paged}
              rowHeight="h-18"
              headerBg="bg-slate-50"
              striped={false}
              tableMinWidth="min-w-[1200px]"
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={onSort}
              status
            />
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {accLoading && (
            <div className="text-sm text-[#1D161680] px-2">กำลังโหลดอุปกรณ์เสริม...</div>
          )}
          <DataTable
            columns={columns}
            data={paged}
            rowHeight="h-18"
            headerBg="bg-slate-50"
            striped={false}
            tableMinWidth="min-w-[900px]"
          />
        </div>
      )}

      {/* footer */}
      <PaginationFooter
        totalItems={totalItems}
        page={safePage}
        pageSize={pageSize}
        onPageChange={(p) => setPage(p)}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setPage(1);
        }}
        pageSizeOptions={[50, 100, 200]}
      />

      {/* modal */}
      <AccessoryUpsertModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        value={selectedAccessory}
        categoryOptions={accCategories}
        onSubmit={async ({ id, data }) => {
          if (!id) {
            try {
              await api.post(PRODUCT_ACCESSORIES_ENDPOINT, { data });
            } catch (firstErr) {
              await api.post(PRODUCT_ACCESSORIES_ENDPOINT_FALLBACK, { data });
            }
          } else {
            try {
              await api.put(`${PRODUCT_ACCESSORIES_ENDPOINT}/${id}`, { data });
            } catch (firstErr) {
              await api.put(`${PRODUCT_ACCESSORIES_ENDPOINT_FALLBACK}/${id}`, { data });
            }
          }
          await fetchProductAccessories();
        }}
      />

      <AccessorySellModal
        open={sellOpen}
        onClose={() => setSellOpen(false)}
        value={selectedSell}
        onSubmit={async (payload) => {
          try {
            const currentQty = Number(selectedSell?.qty ?? selectedSell?.raw?.qty ?? 0) || 0;
            if (currentQty <= 0) {
              alert("สินค้าหมดสต๊อก");
              return;
            }
            try {
              await api.post(`${PRODUCT_ACCESSORIES_ENDPOINT}/sell`, { data: payload });
            } catch (firstErr) {
              await api.post(`${PRODUCT_ACCESSORIES_ENDPOINT_FALLBACK}/sell`, { data: payload });
            }
            await fetchProductAccessories();
            setSellOpen(false);
          } catch (err) {
            console.error(err);
            alert("บันทึกการขายไม่สำเร็จ");
          }
        }}
      />
    </div>
  );
}